import { Box, Card, Grid, Stack, Typography, CircularProgress, Alert, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse, IconButton, TablePagination } from "@mui/material";
import { AccountBalance, Payments, ArrowForward, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import pxToRem from "../../../../Themes/Functions/pxToRem";
import uuid from "react-uuid";
import moment from "moment";
import { noScrollBarStyle } from "../../../../Configurations/AppStyle";
import { useEffect, useState } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useSelector, useDispatch } from "react-redux";
import DateRangeFilter from "../MyWallet/components/DateRangeFilter";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import TicketMapping from "../MyWallet/panels/TicketMapping";

const ServicePartnerPaymentPage = () => {
  const [ticketPayments, setTicketPayments] = useState([]);
  const [ticketMappings, setTicketMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [error, setError] = useState(null);
  const auth = useSelector((state) => state.auth);
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [financeData, setFinanceData] = useState({
    pendingPartnerPayments: 0,
    pendingPartnerCount: 0,
    totalBankBalance: 0,
    bankAccountCount: 0
  });
  const [currentTab, setCurrentTab] = useState(0);
  const dispatch = useDispatch();

  // Partner filter state
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  // Pending approvals state
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPartnerFilter, setPendingPartnerFilter] = useState('');
  // SELECT TICKET & PAYMENT tab: partner-gated paginated mapping fetch.
  // Empty filter means the page does NOT call /v1/tickets/mapping — the old
  // behavior of loading every COMPLETED ticket across all partners is gone.
  const [mappingPartnerFilter, setMappingPartnerFilter] = useState('');
  const [mappingPage, setMappingPage] = useState(0);
  const [mappingPageSize, setMappingPageSize] = useState(20);
  const [mappingTotal, setMappingTotal] = useState(0);
  const [mappingLoading, setMappingLoading] = useState(false);

  // Get organization ID from multiple possible sources
  const getOrganizationId = () => {
    if (auth?.user?.organisation?.id) return auth.user.organisation.id;
    if (auth?.user?.organisationId) return auth.user.organisationId;

    const localStorageOrgId = localStorage.getItem('organizationId');
    if (localStorageOrgId) return localStorageOrgId;

    try {
      const localStorageUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (localStorageUser?.organisation?.id) return localStorageUser.organisation.id;
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }

    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      if (authInfo?.principalInfo?.organisationId) return authInfo.principalInfo.organisationId;
      if (authInfo?.user?.organisation?.id) return authInfo.user.organisation.id;
    } catch (e) {
      console.error('Error parsing authInfo from localStorage:', e);
    }

    return null;
  };

  const organisationId = getOrganizationId();

  // Update page title
  useEffect(() => {
    dispatch(updateScreenTitle("Service Partner Payments"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Load finance overview data for pending partner payments.
  // Only the bank-wallet and service-partner sub-sections are needed; calling
  // them in parallel avoids the 30s+ monolithic /overview aggregation.
  useEffect(() => {
    const fetchFinanceData = async () => {
      setFinanceLoading(true);
      try {
        const [bank, partner] = await Promise.all([
          AppService.get("v1/admin/finance/overview/bank-wallet").catch(() => null),
          AppService.get("v1/admin/finance/overview/service-partner").catch(() => null)
        ]);
        setFinanceData({
          pendingPartnerPayments: partner?.pendingApprovalServicePartnerAmount || 0,
          pendingPartnerCount: partner?.pendingApprovalServicePartnerCount || 0,
          totalBankBalance: bank?.totalBankBalance || 0,
          bankAccountCount: bank?.activeBankAccounts || 0
        });
      } catch (error) {
        console.error("Error fetching finance overview:", error);
      } finally {
        setFinanceLoading(false);
      }
    };

    fetchFinanceData();
  }, []);

  // Function to fetch ticket mappings for the currently selected partner.
  // Always sends partnerOrgId + page + pageSize and consumes the paginated
  // response shape { items, totalElements, ... } from /v1/tickets/mapping.
  const fetchTicketMappings = () => {
    if (!mappingPartnerFilter) {
      setTicketMappings([]);
      setMappingTotal(0);
      return;
    }
    setMappingLoading(true);
    AppService.get("v1/tickets/mapping", {
      params: {
        partnerOrgId: mappingPartnerFilter,
        page: mappingPage,
        pageSize: mappingPageSize,
      },
    })
      .then((response) => {
        const items = Array.isArray(response?.items) ? response.items : [];
        const total = Number(response?.totalElements) || 0;
        setTicketMappings(items);
        setMappingTotal(total);
      })
      .catch((error) => {
        console.error("Error fetching ticket mappings:", error);
        setTicketMappings([]);
        setMappingTotal(0);
      })
      .finally(() => setMappingLoading(false));
  };

  // Re-fetch the slice whenever the partner, page, or page size changes.
  // No partner → no request on initial load; the unpaid-ticket table stays
  // empty until the admin picks a partner.
  useEffect(() => {
    fetchTicketMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingPartnerFilter, mappingPage, mappingPageSize]);

  // Load pending approval transactions
  useEffect(() => {
    setPendingLoading(true);
    AppService.get("v2/partner-payment-transactions/pending")
      .then((response) => {
        setPendingApprovals(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error("Error fetching pending approvals:", error);
        setPendingApprovals([]);
      })
      .finally(() => setPendingLoading(false));
  }, []);

  // Load partner organizations for filter
  useEffect(() => {
    AppService.get("v1/organisation/keyValues?type=PARTNER")
      .then((response) => {
        const list = Array.isArray(response) ? response : response?.data || [];
        setPartners(list);
      })
      .catch((error) => {
        console.error("Error loading partner organizations:", error);
        setPartners([]);
      });
  }, []);

  // Filter payments by date range and partner
  const filteredPayments = ticketPayments.filter(payment => {
    // Apply partner filter
    if (selectedPartnerId && payment.partnerOrganisationId !== selectedPartnerId) {
      return false;
    }

    // Apply date filter
    if (!dateFilter.startDate && !dateFilter.endDate) return true;

    const paymentDate = moment(payment.createdAt || payment.initiatedAt);
    const start = dateFilter.startDate ? moment(dateFilter.startDate) : null;
    const end = dateFilter.endDate ? moment(dateFilter.endDate).endOf('day') : null;

    if (start && end) {
      return paymentDate.isBetween(start, end, null, '[]');
    } else if (start) {
      return paymentDate.isSameOrAfter(start);
    } else if (end) {
      return paymentDate.isSameOrBefore(end);
    }
    return true;
  });

  useEffect(() => {
    fetchTicketPaymentDetails();
  }, []);

  const fetchTicketPaymentDetails = () => {
    setLoading(true);
    setError(null);

    AppService.get(`v2/partner-payment-transactions`)
      .then((response) => {
        console.log("Fetched partner payment transactions:", response);
        setTicketPayments(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error("Error fetching service partner payment history:", error);
        setTicketPayments([]);
        setError("Error fetching payment history");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Metric Tile Component
  const MetricTile = ({ title, amount, subtitle, icon: Icon, color, onClick }) => (
    <Card
      sx={{
        background: color,
        borderRadius: 2,
        p: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        } : {},
        position: 'relative',
        overflow: 'hidden',
        minHeight: 130
      }}
      onClick={onClick}
    >
      {/* Icon in top right */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          opacity: 0.15,
          color: 'white'
        }}
      >
        <Icon sx={{ fontSize: 42 }} />
      </Box>

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: 'rgba(255, 255, 255, 0.95)',
            fontWeight: 500,
            mb: 1.5,
            fontSize: '0.75rem'
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            color: '#fff',
            fontWeight: 700,
            mb: 0.75,
            fontSize: '1.4rem',
            lineHeight: 1.2
          }}
        >
          {amount}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '0.65rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          {subtitle}
          {onClick && <ArrowForward sx={{ fontSize: 14 }} />}
        </Typography>
      </Box>
    </Card>
  );

  const PendingApprovalRow = ({ item }) => {
    const [open, setOpen] = useState(false);
    const hasTickets = item.tickets && item.tickets.length > 0;
    const ticketCount = item.tickets?.length || 0;

    return (
      <>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell>
            {hasTickets && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            )}
          </TableCell>
          <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>{item.referenceNumber || "—"}</TableCell>
          <TableCell sx={{ fontSize: 13 }}>
            {item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD HH:mm") : item.initiatedAt ? moment(item.initiatedAt).format("YYYY-MM-DD HH:mm") : "—"}
          </TableCell>
          <TableCell sx={{ fontSize: 13 }}>{item.partnerOrganisationName || "—"}</TableCell>
          <TableCell align="center">
            <Chip label={`${ticketCount} ticket${ticketCount !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main', fontSize: 14 }}>
            {item.amount ? Number(item.amount).toFixed(2) : "0.00"} AED
          </TableCell>
          <TableCell align="center">
            <Chip label="PENDING APPROVAL" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }} />
          </TableCell>
        </TableRow>
        {hasTickets && (
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ m: 2, bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Tickets in this transaction</Typography>
                  {item.tickets.map((ticket, idx) => (
                    <Box key={ticket.ticketId || idx} display="flex" alignItems="center" sx={{ py: 0.5 }}>
                      <Typography fontSize={12} fontWeight={500} sx={{ minWidth: 70 }}>
                        {ticket.ticketNumber || "—"}
                      </Typography>
                      <Typography fontSize={12} color="primary.main" sx={{ minWidth: 100 }}>
                        {ticket.orgTicketNumber || "—"}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary" sx={{ flex: 1 }}>
                        {ticket.serviceName || "—"}
                      </Typography>
                      <Typography fontSize={12} fontWeight={500} sx={{ minWidth: 80, textAlign: 'right' }}>
                        {ticket.ticketAmount ? Number(ticket.ticketAmount).toFixed(2) : "0.00"} AED
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const PaymentItem = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const hasTickets = item.tickets && item.tickets.length > 0;
    const ticketCount = item.tickets?.length || 0;

    const getStatusColor = (status) => {
      switch (status) {
        case 'COMPLETED':
        case 'CONFIRMED':
        case 'PAID':
          return 'success.main';
        case 'APPROVED':
        case 'PROCESSING':
        case 'TRANSFER_INITIATED':
          return 'info.main';
        case 'PENDING_APPROVAL':
        case 'PENDING':
          return 'warning.main';
        case 'REJECTED':
        case 'FAILED':
        case 'UNPAID':
          return 'error.main';
        default:
          return 'text.secondary';
      }
    };

    // Format status for display
    const formatStatus = (status) => {
      if (!status) return 'UNKNOWN';
      return status.replace(/_/g, ' ');
    };

    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            py: 1.5,
            px: 2,
            cursor: hasTickets ? 'pointer' : 'default',
            '&:hover': hasTickets ? { bgcolor: 'action.hover' } : {}
          }}
          onClick={() => hasTickets && setExpanded(!expanded)}
        >
          {/* Transaction Number - use referenceNumber */}
          <Box sx={{ width: '18%' }}>
            <Typography fontSize={12} fontWeight={500} color="text.secondary">
              {item.referenceNumber || "—"}
            </Typography>
          </Box>
          {/* Date - use createdAt or initiatedAt */}
          <Box sx={{ width: '10%' }}>
            <Typography fontSize={13} fontWeight={500}>
              {item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD") :
               item.initiatedAt ? moment(item.initiatedAt).format("YYYY-MM-DD") : "N/A"}
            </Typography>
          </Box>
          {/* Ticket Count - calculated from tickets array */}
          <Box sx={{ width: '8%' }}>
            <Typography
              fontSize={13}
              color="primary.main"
              sx={{ cursor: hasTickets ? 'pointer' : 'default', '&:hover': hasTickets ? { textDecoration: 'underline' } : {} }}
            >
              {ticketCount} ticket{ticketCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          {/* Partner Name */}
          <Box sx={{ width: '14%' }}>
            <Typography fontSize={13} color="text.secondary" noWrap>
              {item.partnerOrganisationName || "—"}
            </Typography>
          </Box>
          {/* Source Bank (From) - use sourceBankAccountName */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.sourceBankAccountName || "—"}
            </Typography>
          </Box>
          {/* Destination Bank (To Partner) - use bankAccount or partner info */}
          <Box sx={{ width: '12%' }}>
            <Typography fontSize={12} color="text.secondary" noWrap>
              {item.partnerBankAccountName || item.partnerOrganisationName || "—"}
            </Typography>
          </Box>
          {/* Amount */}
          <Box sx={{ width: '13%', textAlign: 'right' }}>
            <Typography fontSize={14} fontWeight={600} color="primary.main">
              {item.amount ? Number(item.amount).toFixed(2) : "0.00"} AED
            </Typography>
          </Box>
          {/* Status - use status field */}
          <Box sx={{ width: '13%', textAlign: 'right' }}>
            <Typography fontSize={12} fontWeight={500} color={getStatusColor(item.status)}>
              {formatStatus(item.status)}
            </Typography>
          </Box>
        </Box>

        {/* Expanded Ticket Details */}
        {expanded && hasTickets && (
          <Box sx={{ bgcolor: 'grey.50', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Typography fontSize={12} fontWeight={600} sx={{ mb: 1 }}>Tickets in this transaction:</Typography>
            {item.tickets.map((ticket, idx) => (
              <Box
                key={ticket.ticketId || idx}
                display="flex"
                alignItems="center"
                sx={{ py: 0.5 }}
              >
                <Typography
                  fontSize={12}
                  color="primary.main"
                  sx={{ minWidth: 100, cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    ticket.ticketId && window.open(`/ticket/${ticket.ticketId}`, '_blank');
                  }}
                >
                  #{ticket.orgTicketNumber || ticket.ticketNumber}
                </Typography>
                <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 180, flex: 1 }}>
                  {ticket.serviceName || "—"}
                </Typography>
                <Typography fontSize={12} fontWeight={500} sx={{ minWidth: 80, textAlign: 'right' }}>
                  {ticket.ticketAmount ? Number(ticket.ticketAmount).toFixed(2) : "0.00"} AED
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${pxToRem(55)})`,
        width: "100%",
      }}
    >
      {/* Metric Tiles */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Total Bank Balance */}
          <Grid item xs={12} md={6}>
            <MetricTile
              title="Total Bank Balance"
              amount={`AED ${Number(financeData.totalBankBalance || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${financeData.bankAccountCount} active account${financeData.bankAccountCount !== 1 ? 's' : ''}`}
              icon={AccountBalance}
              color="#1a7f8f"
            />
          </Grid>

          {/* Pending Service Partner Payments */}
          <Grid item xs={12} md={6}>
            <MetricTile
              title="Pending Partner Payments"
              amount={`AED ${Number(financeData.pendingPartnerPayments || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${financeData.pendingPartnerCount} partner${financeData.pendingPartnerCount !== 1 ? 's' : ''} • Awaiting approval`}
              icon={Payments}
              color="#1f6b7a"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                sx={{ px: 2 }}
              >
                <Tab label="SELECT TICKET & PAYMENT" />
                <Tab label={`PENDING APPROVAL${pendingApprovals.length > 0 ? ` (${pendingApprovals.length})` : ''}`} />
                <Tab label="PAYMENT HISTORY" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Tab 0: Ticket Selection & Payment */}
              {currentTab === 0 && (
                <Box sx={{ p: 2 }}>
                  {/* Partner selector — required to load the unpaid tickets list */}
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 280 }}>
                      <InputLabel id="mapping-partner-filter-label">Partner Organization</InputLabel>
                      <Select
                        labelId="mapping-partner-filter-label"
                        value={mappingPartnerFilter}
                        onChange={(e) => {
                          setMappingPage(0);
                          setMappingPartnerFilter(e.target.value);
                        }}
                        label="Partner Organization"
                      >
                        <MenuItem value="">Select a partner to view</MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner.id || partner.value} value={partner.id || partner.value}>
                            {partner.name || partner.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {mappingPartnerFilter && (
                      <Typography variant="body2" color="text.secondary">
                        {mappingTotal} unpaid ticket{mappingTotal !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>

                  {!mappingPartnerFilter ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      Select a partner to view unpaid tickets
                    </Typography>
                  ) : mappingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TicketMapping
                        mappings={ticketMappings}
                        bankBalance={financeData.totalBankBalance || 0}
                        selectedOrgId={mappingPartnerFilter}
                        hidePagination
                      />
                      <TablePagination
                        component="div"
                        count={mappingTotal}
                        page={mappingPage}
                        onPageChange={(_, newPage) => setMappingPage(newPage)}
                        rowsPerPage={mappingPageSize}
                        onRowsPerPageChange={(e) => {
                          setMappingPageSize(parseInt(e.target.value, 10));
                          setMappingPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100, 200]}
                      />
                    </>
                  )}
                </Box>
              )}

              {/* Tab 1: Pending Approval */}
              {currentTab === 1 && (
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Pending Partner Approval</Typography>
                    {pendingApprovals.length > 0 && (
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#e65100' }}>
                        Processing Total: AED {pendingApprovals.reduce((sum, item) => sum + Number(item.amount || 0), 0).toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                    These payments have been initiated and are waiting for the service partner to approve. Once approved, the amount will be transferred.
                  </Alert>
                  {/* Partner Filter */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel id="pending-partner-filter-label">Partner Organization</InputLabel>
                      <Select
                        labelId="pending-partner-filter-label"
                        value={pendingPartnerFilter}
                        onChange={(e) => setPendingPartnerFilter(e.target.value)}
                        label="Partner Organization"
                      >
                        <MenuItem value="">All Partners</MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner.id || partner.value} value={partner.id || partner.value}>
                            {partner.name || partner.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {(() => {
                    if (pendingLoading) {
                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      );
                    }
                    const filteredPending = pendingPartnerFilter
                      ? pendingApprovals.filter(item => item.partnerOrganisationId === pendingPartnerFilter)
                      : pendingApprovals;
                    if (filteredPending.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          No pending approvals{pendingPartnerFilter ? ' for selected partner' : ''}
                        </Typography>
                      );
                    }
                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#064d51' }}>
                              <TableCell sx={{ color: 'white', width: 50 }} />
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }}>Reference #</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }}>Partner</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }} align="center">Tickets</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }} align="right">Amount</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: 12 }} align="center">Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredPending.map((item) => (
                              <PendingApprovalRow key={item.id || item.referenceNumber} item={item} />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Box>
              )}

              {/* Tab 2: Service Partner Payment History */}
              {currentTab === 2 && (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6">Service Partner Payment History</Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Filters Section */}
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    {/* Partner Filter */}
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel id="partner-filter-label">Filter by Partner</InputLabel>
                      <Select
                        labelId="partner-filter-label"
                        value={selectedPartnerId}
                        onChange={(e) => setSelectedPartnerId(e.target.value)}
                        label="Filter by Partner"
                      >
                        <MenuItem value="">All Partners</MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner.id || partner.value} value={partner.id || partner.value}>
                            {partner.name || partner.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Date Range Filter */}
                    <DateRangeFilter onFilterChange={setDateFilter} />
                  </Box>

                  {/* Table Header */}
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    sx={{ py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
                  >
                    <Box sx={{ width: '18%' }}>
                      <Typography fontSize={12} fontWeight={600}>Txn #</Typography>
                    </Box>
                    <Box sx={{ width: '10%' }}>
                      <Typography fontSize={12} fontWeight={600}>Date</Typography>
                    </Box>
                    <Box sx={{ width: '8%' }}>
                      <Typography fontSize={12} fontWeight={600}>Tickets</Typography>
                    </Box>
                    <Box sx={{ width: '14%' }}>
                      <Typography fontSize={12} fontWeight={600}>Partner</Typography>
                    </Box>
                    <Box sx={{ width: '12%' }}>
                      <Typography fontSize={12} fontWeight={600}>From Bank</Typography>
                    </Box>
                    <Box sx={{ width: '12%' }}>
                      <Typography fontSize={12} fontWeight={600}>To Bank</Typography>
                    </Box>
                    <Box sx={{ width: '13%', textAlign: 'right' }}>
                      <Typography fontSize={12} fontWeight={600}>Amount</Typography>
                    </Box>
                    <Box sx={{ width: '13%', textAlign: 'right' }}>
                      <Typography fontSize={12} fontWeight={600}>Status</Typography>
                    </Box>
                  </Box>

                  {/* Payment List */}
                  <Box sx={{ flexGrow: 1, overflow: 'auto', ...noScrollBarStyle }}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                      </Box>
                    ) : ticketPayments.length > 0 ? (
                      <Stack sx={{ overflow: "auto", flexGrow: 1 }}>
                        {filteredPayments.length > 0 ? (
                          filteredPayments.map((payment) => (
                            <PaymentItem key={uuid()} item={payment} />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            No payments found for the selected date range
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                        No payment history found
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicePartnerPaymentPage;
